/**
 * PreviewSidebarPanel (v4.16) — "Preview" toolbar button target.
 *
 * Right slide-out that renders the SELECTED canvas ads (or all ads when
 * nothing is selected) as native Facebook feed posts — page logo + name +
 * "Sponsored", primary text, the creative, link-card strip with headline +
 * CTA, Like/Comment/Share row — vertically scrollable, one post per ad.
 *
 * Copy resolution per ad (best available first):
 *   1. Live store ad-copy variations (canvasAdCopy — populated by copy_complete SSE)
 *   2. Persisted canvas_ad_copy variations (GET /api/canvas/projects/:id/ad-copy —
 *      full-funnel writes 3 per image; store is NOT hydrated on reload, so we fetch)
 *   3. The image row's own baked-in headline / bodyCopy / ctaText (from ad angles)
 *
 * Wrapper pattern cloned from CopySidebarPanel (fixed right, translateX slide,
 * zIndex 250 — under the toolbar pill at 600, fine at this width).
 */
import { useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { X } from 'lucide-react';
import type { Brand } from '@shared/schema';
import { useCanvasStore } from './canvas-store';
import type { AdCopyVariation } from './canvas-store';
import { useActiveProjectImages, useActiveProjectCanvasAdCopy } from './store-selectors';
import type { CanvasImage } from './canvas-types';
import { MetaAdPreview } from '@/components/ads-manager/MetaAdPreview';

interface PreviewSidebarPanelProps {
  open: boolean;
  onClose: () => void;
  projectId: number | null;
}

export function PreviewSidebarPanel({ open, onClose, projectId }: PreviewSidebarPanelProps) {
  const images = useActiveProjectImages();
  const selectedImageIds = useCanvasStore((s) => s.selectedImageIds);
  const storeAdCopy = useActiveProjectCanvasAdCopy();
  const activeBrandId = useCanvasStore((s) => s.activeBrandId);
  const projectBrandId = useCanvasStore((s) =>
    s.activeProjectId != null ? s.projects[s.activeProjectId]?.brandId ?? null : null,
  );

  // Brand for the FB page header (name + logo + website). /api/brands is
  // already cached app-wide by canvas-chat, so this query is effectively free.
  const { data: brands = [] } = useQuery<Brand[]>({ queryKey: ['/api/brands'], staleTime: 60_000 });
  // v3.87-style fallback (mirrors canvas-chat's brand chip): on a REOPENED
  // canvas the store's activeBrandId is only lazily set at first send and the
  // per-project brandId may not be hydrated — the server project row is the
  // ground truth. Without this the header showed "Your Brand" / example.com.
  const { data: serverProject } = useQuery<{ id: number; name: string; brandId?: number | null }>({
    queryKey: ['/api/canvas/projects', projectId],
    queryFn: async () => {
      const resp = await fetch(`/api/canvas/projects/${projectId}`);
      if (!resp.ok) return null as never;
      return resp.json();
    },
    staleTime: 60_000,
    enabled: open && projectId != null && activeBrandId == null && projectBrandId == null,
  });
  const brand = brands.find(
    (b) => b.id === (activeBrandId ?? projectBrandId ?? serverProject?.brandId ?? -1),
  );
  const landingPageUrl = brand?.website
    ? brand.website.startsWith('http') ? brand.website : `https://${brand.website}`
    : null;

  // Persisted copy variations — the store map is only populated by live SSE,
  // so after a reload (or for full-funnel's DB-inserted copy) fetch directly.
  const { data: persistedCopy } = useQuery<{
    grouped_by_image: Array<{ imageClientId: string; variations: AdCopyVariation[] }>;
  }>({
    queryKey: [`/api/canvas/projects/${projectId}/ad-copy`],
    enabled: open && projectId != null,
    staleTime: 30_000,
  });
  const persistedByImage = useMemo(() => {
    const map = new Map<string, AdCopyVariation[]>();
    for (const g of persistedCopy?.grouped_by_image ?? []) {
      if (g.imageClientId && Array.isArray(g.variations) && g.variations.length > 0) {
        map.set(g.imageClientId, g.variations);
      }
    }
    return map;
  }, [persistedCopy]);

  // Selection drives the list; no selection = preview every ad in the project.
  // campaign-v3's off-screen race-prevention spacer row must never render.
  const targets = useMemo(() => {
    const pool = images.filter((img) => img.name !== 'campaign-v3-spacer');
    if (selectedImageIds.length === 0) return pool;
    return pool.filter((img) => selectedImageIds.includes(img.id));
  }, [images, selectedImageIds]);

  // Copy for one image, INHERITING up the variant chain: an edited tile
  // (surgical remix / edit-fixes "-v2") carries no copy of its own, but its
  // SOURCE ad does — same creative lineage, same copy. Walk sourceImageId
  // until a hop has any copy signal (bounded + cycle-safe). Deliberately NO
  // conceptName/name fallback for the headline: an angle label like
  // "Pain Point Angle" rendered as the ad headline reads as a bug — a truly
  // copyless ad shows MetaAdPreview's neutral placeholder instead.
  const resolveCopy = (img: CanvasImage) => {
    const visited = new Set<string>();
    let cur: CanvasImage | undefined = img;
    for (let hop = 0; cur && hop < 4 && !visited.has(cur.id); hop++) {
      visited.add(cur.id);
      const storeEntry = storeAdCopy[cur.id];
      const storeVar =
        storeEntry?.variations?.[storeEntry.activeVariationIndex] ?? storeEntry?.variations?.[0];
      const persistedVar = persistedByImage.get(cur.id)?.[0];
      const v = storeVar ?? persistedVar;
      if (v || cur.headline || cur.bodyCopy) {
        return {
          headline: v?.headline ?? cur.headline ?? null,
          body: v?.primaryText ?? cur.bodyCopy ?? null,
          cta: v?.cta ?? cur.ctaText ?? 'Shop Now',
        };
      }
      cur = cur.sourceImageId ? images.find((i) => i.id === cur!.sourceImageId) : undefined;
    }
    return { headline: null, body: null, cta: img.ctaText ?? 'Shop Now' };
  };

  const ratioFor = (img: CanvasImage): 'square' | 'portrait' =>
    img.originalWidth > 0 && img.originalHeight / img.originalWidth >= 1.15 ? 'portrait' : 'square';

  return (
    <div
      data-testid="preview-sidebar-panel"
      style={{
        position: 'fixed',
        top: 0,
        right: 0,
        bottom: 0,
        width: 440,
        background: '#F1F5F9',
        boxShadow: '-4px 0 24px rgba(0,0,0,0.12)',
        zIndex: 250,
        display: 'flex',
        flexDirection: 'column',
        transform: open ? 'translateX(0)' : 'translateX(100%)',
        transition: 'transform 0.3s cubic-bezier(0.32, 0, 0.15, 1)',
      }}
    >
      {/* Header */}
      <div
        style={{
          flexShrink: 0,
          padding: '14px 16px',
          borderBottom: '1px solid #DCE3ED',
          background: '#FFFFFF',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
        }}
      >
        <div>
          <div style={{ fontWeight: 700, fontSize: 14, color: '#111827' }}>Facebook Preview</div>
          <div style={{ fontSize: 12, color: '#5F6D82', marginTop: 2 }}>
            {targets.length} ad{targets.length === 1 ? '' : 's'}
            {selectedImageIds.length === 0 && targets.length > 0 ? ' · all (none selected)' : ''}
          </div>
        </div>
        <button
          data-testid="preview-sidebar-close"
          onClick={onClose}
          style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#5F6D82', padding: 4 }}
          aria-label="Close preview"
        >
          <X size={16} />
        </button>
      </div>

      {/* Scrollable feed */}
      <div style={{ flex: 1, overflowY: 'auto', padding: '20px 0' }}>
        {targets.length === 0 ? (
          <div style={{ padding: '48px 24px', textAlign: 'center', fontSize: 13, color: '#5F6D82' }}>
            No ads to preview yet — select ads on the canvas, or generate some first.
          </div>
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 20 }}>
            {targets.map((img) => {
              const copy = resolveCopy(img);
              return (
                <MetaAdPreview
                  key={img.id}
                  headline={copy.headline}
                  body={copy.body}
                  callToAction={copy.cta}
                  imageUrl={img.src}
                  landingPageUrl={landingPageUrl}
                  creativeType="image"
                  accountName={brand?.name ?? 'Your Brand'}
                  logoUrl={brand?.logo ?? null}
                  aspectRatio={ratioFor(img)}
                />
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}
